import React from 'react';
import './Pagination.css';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  variant?: 'default' | 'analytics';
}

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [1];

  if (current > 3) {
    pages.push('...');
  }

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push('...');
  }

  pages.push(total);
  return pages;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  variant = 'default',
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = getPageNumbers(currentPage, totalPages);
  const analytics = variant === 'analytics';

  const btnBase: React.CSSProperties = {
    padding: analytics ? '7px 10px' : '6px 12px',
    borderRadius: analytics ? '5px' : '4px',
    cursor: 'pointer',
    fontSize: analytics ? '11px' : '14px',
    minWidth: '36px',
    textAlign: 'center',
  };

  const disabledStyle: React.CSSProperties = {
    ...btnBase,
    cursor: 'not-allowed',
    opacity: 0.4,
  };

  const activeStyle: React.CSSProperties = {
    ...btnBase,
    fontWeight: 600,
  };

  const isFirst = currentPage === 1;
  const isLast = currentPage === totalPages;

  return (
    <div
      className={`shared-pagination${analytics ? ' shared-pagination--analytics' : ''}`}
      style={{
        marginTop: analytics ? '12px' : '20px',
        paddingTop: analytics ? '12px' : undefined,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        flexWrap: 'wrap',
      }}
    >
      <button
        className="shared-pagination__button"
        onClick={() => onPageChange(1)}
        disabled={isFirst}
        style={isFirst ? disabledStyle : btnBase}
        title="First page"
        aria-label="First page"
      >
        «
      </button>
      <button
        className="shared-pagination__button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={isFirst}
        style={isFirst ? disabledStyle : btnBase}
        title="Previous page"
        aria-label="Previous page"
      >
        ‹
      </button>

      {pages.map((page, idx) =>
        page === '...' ? (
          <span className="shared-pagination__ellipsis" key={`ellipsis-${idx}`} style={{ padding: '6px 8px', fontSize: '14px' }}>
            …
          </span>
        ) : (
          <button
            className={`shared-pagination__button${page === currentPage ? ' shared-pagination__button--active' : ''}`}
            key={page}
            onClick={() => onPageChange(page)}
            style={page === currentPage ? activeStyle : btnBase}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        ),
      )}

      <button
        className="shared-pagination__button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={isLast}
        style={isLast ? disabledStyle : btnBase}
        title="Next page"
        aria-label="Next page"
      >
        ›
      </button>
      <button
        className="shared-pagination__button"
        onClick={() => onPageChange(totalPages)}
        disabled={isLast}
        style={isLast ? disabledStyle : btnBase}
        title="Last page"
        aria-label="Last page"
      >
        »
      </button>
    </div>
  );
}
